import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import { FormikErrors, FormikProps } from "formik";
import { CommodityType, CompanyDetails, UpdateValues } from "./index";
import { FormControl, MenuItem, Select, TextField } from "@mui/material";
import { memo } from "react";
import DynamicAutocomplete from "@/components/common/inputs/select/DynamicAutocomplete";

interface RenderOtherDetailsProps {
  userType: UserTierType;
  formik: FormikProps<UpdateValues>;
  commodityList: CommodityType[];
}

const RenderOtherDetails: React.FC<RenderOtherDetailsProps> = memo(
  ({ userType, formik, commodityList }) => {
    switch (userType) {
      case UserTierType.Tier4:
        return (
          <div className="">
            <div className="tw-grid tw-grid-cols-3 tw-gap-6">
              <div>
                <label className="tw-block tw-text-sm font-gray-color-dark tw-font-medium tw-mb-2">
                  Facility Required?
                </label>
                <div className="tw-mt-2">
                  <label className="tw-mr-4">
                    <input
                      type="radio"
                      name="companies.0.facility_required"
                      value="true"
                      checked={
                        formik.values.companies[0].facility_required === true
                      }
                      onChange={() =>
                        formik.setFieldValue(
                          "companies.0.facility_required",
                          true,
                        )
                      }
                      className="tw-mr-2"
                    />
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="companies.0.facility_required"
                      value="false"
                      checked={
                        formik.values.companies[0].facility_required === false
                      }
                      onChange={() =>
                        formik.setFieldValue(
                          "companies.0.facility_required",
                          false,
                        )
                      }
                      className="tw-mr-2"
                    />
                    No
                  </label>
                </div>
              </div>
              {formik.values.companies[0].facility_required && (
                <div className="form-group">
                  <label className="tw-block tw-text-sm font-gray-color-dark tw-font-medium tw-mb-2">
                    Facility Amount
                  </label>
                  <TextField
                    fullWidth
                    type="number"
                    name="companies.0.facility_amount"
                    variant="outlined"
                    placeholder="Enter Amount"
                    className="tw-w-full tw-h-11 tw-px-3 tw-border tw-border-[#D5D7DA] tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500"
                    value={formik.values.companies[0].facility_amount}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.companies?.[0]?.facility_amount &&
                      Boolean(
                        (
                          formik.errors
                            .companies?.[0] as FormikErrors<CompanyDetails>
                        )?.facility_amount,
                      )
                    }
                    helperText={
                      formik.touched.companies?.[0]?.facility_amount &&
                      (
                        formik.errors
                          .companies?.[0] as FormikErrors<CompanyDetails>
                      )?.facility_amount
                    }
                  />
                </div>
              )}
            </div>
          </div>
        );

      case UserTierType.Tier5:
        return (
          <div className="custom-card-body">
            <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-6 md:tw-gap-4 tw-gap-2">
              <FormControl fullWidth className="form-group">
                <p className="tw-text-sm font-gray-color tw-font-medium tw-mb-2">
                  Debtor Type
                </p>
                <Select
                  name="companies.0.debtor_type"
                  value={formik.values.companies[0].debtor_type || ""}
                  onChange={formik.handleChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    <span className="tw-text-gray-400">Select Debtor Type</span>
                  </MenuItem>
                  <MenuItem value={1}>Consumer</MenuItem>
                  <MenuItem value={2}>Business</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth className="form-group">
                <p className="tw-text-sm font-gray-color tw-font-medium tw-mb-2">
                  Payment Terms
                </p>
                <Select
                  name="companies.0.payment_terms"
                  value={formik.values.companies[0].payment_terms || ""}
                  onChange={formik.handleChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    <span className="tw-text-gray-400">
                      Select Payment Terms
                    </span>
                  </MenuItem>
                  <MenuItem value={1}>Other</MenuItem>
                  <MenuItem value={2}>30 Days</MenuItem>
                  <MenuItem value={3}>60 Days</MenuItem>
                </Select>
                {formik.values.companies[0].payment_terms === 1 && (
                  <div className="tw-pt-3 tw-bg-[#F8FAFC] tw-rounded-br-md tw-rounded-bl-md tw-p-3 tw-border tw-border-[#E9EAEB]  tw-border-t-0">
                    <p className="tw-text-sm font-gray-color tw-font-medium tw-mb-2">
                      Number of Days
                    </p>
                    <TextField
                      fullWidth
                      className="tw-mt-4"
                      name="companies.0.custom_payment_days"
                      placeholder="Enter Number of days"
                      variant="outlined"
                      value={
                        formik.values.companies[0].custom_payment_days || ""
                      }
                      onChange={formik.handleChange}
                      error={Boolean(
                        formik.touched.companies?.[0]?.custom_payment_days &&
                          (
                            formik.errors
                              .companies?.[0] as FormikErrors<CompanyDetails>
                          )?.custom_payment_days,
                      )}
                      helperText={
                        formik.touched.companies?.[0]?.custom_payment_days &&
                        (
                          formik.errors
                            .companies?.[0] as FormikErrors<CompanyDetails>
                        )?.custom_payment_days
                      }
                    />
                  </div>
                )}
              </FormControl>

              <div className="form-group">
                <p className="tw-text-sm font-gray-color tw-font-medium tw-mb-2">
                  Client Type
                </p>
                <FormControl fullWidth>
                  <Select
                    name="companies.0.client_type"
                    value={formik.values.companies[0].client_type || ""}
                    onChange={formik.handleChange}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      <span className="tw-text-gray-400">
                        Select Client Type
                      </span>
                    </MenuItem>
                    <MenuItem value={1}>ACC (Account)</MenuItem>
                    <MenuItem value={2}>Cash</MenuItem>
                  </Select>
                </FormControl>
                {formik.values.companies[0].client_type === 1 && (
                  <div className="tw-pt-3 tw-bg-[#F8FAFC] tw-rounded-br-md tw-rounded-bl-md tw-p-3 tw-border tw-border-[#E9EAEB]  tw-border-t-0 tw-mb-2">
                    <p className="tw-text-sm font-gray-color tw-font-medium tw-mb-2">
                      Credit Facility
                    </p>
                    <TextField
                      fullWidth
                      className="tw-mt-4"
                      name="companies.0.credit_facility"
                      placeholder="Enter Credit Facility"
                      variant="outlined"
                      value={formik.values.companies[0].credit_facility || ""}
                      onChange={formik.handleChange}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case UserTierType.Tier6:
        return (
          <div className="custom-card-body">
            <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-6 md:tw-gap-4 tw-gap-2">
              <FormControl fullWidth>
                <p className="tw-text-sm font-gray-color tw-font-medium tw-mb-2">
                  Profession
                </p>
                <Select
                  name="companies.0.profession"
                  value={formik.values.companies[0].profession}
                  onChange={formik.handleChange}
                >
                  <MenuItem value={1}>Profession 1</MenuItem>
                  <MenuItem value={2}>Profession 2</MenuItem>
                </Select>
              </FormControl>

              <div>
                <p className="tw-text-sm font-gray-color tw-font-medium tw-mb-2">
                  Why do you want to access Sentinel?
                </p>
                <TextField
                  fullWidth
                  name="companies.0.reason_to_access"
                  variant="outlined"
                  placeholder="Enter your reason"
                  value={formik.values.companies[0].reason_to_access}
                  onChange={formik.handleChange}
                />
              </div>
            </div>
          </div>
        );

      case UserTierType.Tier7:
        return (
          <div className="custom-card-body">
            <p className="tw-text-sm font-gray-color tw-font-medium tw-mb-2">
              Commodities
            </p>
            <DynamicAutocomplete
              name="companies.0.commodity_types"
              label="Commodities"
              multiple
              onChange={(value: any) => {
                formik.setFieldValue("companies.0.commodity_types", value);
              }}
              onBlur={() => formik.handleBlur("companies.0.commodity_types")}
              options={commodityList}
              selectedValue={formik.values.companies[0].commodity_types || []}
              labelKey="title"
              valueKey="id"
              error={
                formik.touched.companies?.[0]?.commodity_types &&
                Boolean(
                  (formik.errors.companies?.[0] as FormikErrors<CompanyDetails>)
                    ?.commodity_types,
                )
                  ? String(
                      (
                        formik.errors
                          .companies?.[0] as FormikErrors<CompanyDetails>
                      )?.commodity_types,
                    )
                  : ""
              }
              helperText={
                formik.touched.companies?.[0]?.commodity_types &&
                (formik.errors.companies?.[0] as FormikErrors<CompanyDetails>)
                  ?.commodity_types
                  ? String(
                      (
                        formik.errors
                          .companies?.[0] as FormikErrors<CompanyDetails>
                      )?.commodity_types,
                    )
                  : ""
              }
            />
            <FormControl fullWidth className="tw-mb-4">
              <Select
                multiple
                name="companies.0.commodity_types"
                value={formik.values.companies[0].commodity_types || []}
                onChange={formik.handleChange}
              >
                {commodityList.map((commodity: CommodityType) => (
                  <MenuItem key={commodity.id} value={commodity.id}>
                    {commodity.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        );

      default:
        return null;
    }
  },
);

RenderOtherDetails.displayName = "RenderOtherDetails";

export default RenderOtherDetails;
